<template>
    <div class="update-company-wrapper">
        <common-content-wrapper width="large">
            <div class="update-company-package">
                <div class="update-company-package__switch">
                    <checkbox-input
                        :label="$t('pages.company.create_company.package_step.monthly')"
                        :secondLabel="$t('pages.company.create_company.package_step.yearly')"
                        :value="updatedCompany.yearlySubscription"
                        type="switch"
                        labelPosition="left"
                        @change="changeSubscription"
                    ></checkbox-input>
                </div>

                <div class="update-company-package__packages">
                    <package-info
                        v-for="packageOption in companyPackages.package_options"
                        :key="packageOption.key"
                        :isActivePackage="isActivePackage(packageOption.class)"
                        :class="getPackageClass(packageOption.class)"
                        :packageOption="packageOption"
                        :isSubsciptionYearly="updatedCompany.yearlySubscription"
                        :packageLabel="'forms.company.create_company.choose_package'"
                        :isDisabled="isChooseButtonDisabled(packageOption.class)"
                        :regularYearly="regularYearly"
                        :regularMonthly="regularMonthly"
                        :subdomainYearly="subdomainYearly"
                        :subdomainMonthly="subdomainMonthly"
                        :currency="currency"
                        @package-chosen="packageChosen"
                    ></package-info>
                </div>
                <p v-if="updatedCompany.requestedSubdomainRemoval" class="subdomain-note">
                    {{
                        $t('forms.company.create_company.subdomain_removal', {
                            removalDate: updatedCompany.requestedSubdomainRemoval,
                        })
                    }}
                </p>
                <div v-if="!isPackageBasic(currentPackage)">
                    <form-row class="update-company__choose-subdomain-form">
                        <form-cell width="full" class="update-company__choose-subdomain-cell">
                            <text-input
                                :formData="data"
                                :label="$t('pages.company.create_company.subdomain')"
                                :placeholder="$t('pages.company.create_company.enter_subdomain')"
                                :isRequired="true"
                                type="text"
                                valuePath="subdomain"
                                @change="change"
                            ></text-input>
                        </form-cell>
                        <form-cell width="third" class="update-company__choose-subdomain-cell">
                            <div class="update-company__choose-subdomain-extension">
                                <input
                                    class="update-company__choose-subdomain-extension__input"
                                    :placeholder="extensionPlacholder"
                                    disabled="disabled"
                                    type="text"
                                />
                            </div>
                        </form-cell>
                    </form-row>
                    <div class="update-company__choose-subdomain-text">
                        <span class="update-company__choose-subdomain-span">*</span>
                        {{ $t('pages.company.create_company.subdomain_footnote') }}
                    </div>
                </div>
                <common-content-spacer
                    v-if="!isPackageBasic(currentPackage)"
                    height="medium"
                    class="update-company-package__theme-card-spacer"
                >
                    <theme-card
                        v-for="(type, index) in getEnums"
                        :key="index"
                        :type="type"
                        :isActive="isActive(type)"
                        @theme-preview="previewTheme"
                        @click="chooseTheme(type)"
                    ></theme-card>
                </common-content-spacer>
            </div>
            <common-pdf-preview v-if="isPreviewPdfOpen" :url="selectedDocument.src">
                <common-buttons align="justify" class="review-pdf__modal-buttons">
                    <common-button
                        class="review-pdf__modal-buttons__button-close"
                        :label="$t('common.microcopy.back')"
                        @click="closePdfPreview"
                    ></common-button>
                    <common-button
                        class="review-pdf__modal-buttons__button-choose"
                        :label="$t('pages.company.create_company.choose_theme')"
                        @click="selectTheme"
                    ></common-button>
                </common-buttons>
            </common-pdf-preview>
            <div class="update-company-package__submit">
                <common-button
                    type="primary"
                    :label="$t('common.microcopy.save')"
                    @click="isCompanyUpdateModalOpen = true"
                ></common-button>
            </div>
            <common-yes-no-modal
                v-if="isCompanyUpdateModalOpen"
                :showWarningIcon="true"
                height="tiny"
                :headingText="$t('forms.company.create_company.update_company_heading')"
                :noButtonText="$t('common.microcopy.close')"
                :yesButtonText="$t('common.microcopy.save')"
                @close="isCompanyUpdateModalOpen = false"
                @continue="updateCompany"
            ></common-yes-no-modal>
            <common-yes-no-modal
                v-if="isDeactivateCompanyModalOpen"
                :showWarningIcon="true"
                height="tiny"
                :headingText="$t('forms.company.create_company.deactivate_company_heading')"
                :noButtonText="$t('common.microcopy.close')"
                :yesButtonText="$t('common.microcopy.save')"
                @close="isDeactivateCompanyModalOpen = false"
                @continue="deactivateCompany"
            ></common-yes-no-modal>
        </common-content-wrapper>
    </div>
</template>

<script>
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import PackageInfo from 'components/page-elements/package/PackageInfo'
    import CompanyCreateEnum from 'enums/company_create.enum'
    import ThemeEnum from 'enums/theme.enum'
    import ThemeCard from 'components/page-elements/subdomain/ThemeCard'
    import CompanyPackagesEnum from 'enums/company_packages.enum'
    import CompanyAPI from 'api/CompanyAPI'
    import FormMixin from 'mixins/FormMixin'
    import { getSocialMediaURL } from '~/helpers'
    import SocialMediaEnums from 'enums/social_media.enum'
    import PackagePriceMixin from 'mixins/PackagePriceMixin'

    export default {
        mixins: [FormMixin, PackagePriceMixin],

        components: {
            CheckboxInput,
            PackageInfo,
            ThemeCard,
        },

        props: {
            company: { type: Object, default: null },
        },

        data() {
            return {
                isSubsciptionYearly: false,
                isChooseThemeActive: false,
                currentPackage: null,
                updatedCompany: null,
                isPreviewPdfOpen: false,
                selectedDocument: null,
                isCompanyUpdateModalOpen: false,
                isDeactivateCompanyModalOpen: false,
                countryName: 'Serbia',
            }
        },

        computed: {
            companyPackages() {
                return CompanyCreateEnum[this.$i18n.locale]
            },

            getEnums() {
                return Object.entries(ThemeEnum).reduce((acc, [key, value]) => {
                    acc[key.toLowerCase()] = value.toLowerCase()
                    return acc
                }, {})
            },
            extensionPlacholder() {
                let url = process.env.VUE_APP_URL
                const regex = /(https?:\/\/)?([^/]+)\//
                const match = url.match(regex)
                return match ? `.${match[2]}` : ''
            },
        },

        methods: {
            changeSubscription() {
                this.updatedCompany.yearlySubscription = !this.updatedCompany.yearlySubscription
            },
            getPackageClass(packageClass) {
                return {
                    ['package-info']: true,
                    [`package-info__${packageClass}`]: true,
                }
            },
            isActivePackage(packageClass) {
                return this.currentPackage === packageClass
            },
            packageChosen(chosenPackage) {
                this.currentPackage = chosenPackage

                if (this.isPackageBasic(chosenPackage)) {
                    this.updatedCompany.subdomain = null
                }
            },
            isPackageBasic(companyPackage) {
                return companyPackage === CompanyPackagesEnum.PACKAGE.BASIC.toLowerCase()
            },
            isActive(type) {
                return this.updatedCompany.theme?.toLowerCase() === type
            },
            previewTheme(theme) {
                this.selectedDocument = {
                    theme,
                    src: `${env('VUE_APP_PUBLIC_CONTENT_BUCKET')}documents/theme-${theme}.pdf`,
                }
                this.isPreviewPdfOpen = true
            },
            closePdfPreview() {
                this.isPreviewPdfOpen = false
            },
            selectTheme() {
                this.updatedCompany.theme = this.selectedDocument.theme.toUpperCase()
                this.closePdfPreview()
            },
            isChooseButtonDisabled(companyPackage) {
                return this.currentPackage === companyPackage
            },
            getCurrentPackage() {
                if (this.updatedCompany.subdomain && !this.updatedCompany.requestedSubdomainRemoval) {
                    this.currentPackage = CompanyPackagesEnum.PACKAGE.PROFESSIONAL.toLowerCase()
                } else {
                    this.currentPackage = CompanyPackagesEnum.PACKAGE.BASIC.toLowerCase()
                }
            },

            changeCountryName() {
                this.countryName = this.company?.city?.country?.name ?? 'Serbia'
            },

            updateCompany() {
                this.$startLoader()
                if (this.isPackageBasic(this.currentPackage)) {
                    this.updatedCompany.subdomain = null
                } else {
                    this.updatedCompany.subdomain = this.data.subdomain
                }
                this.updatedCompany.cityId = this.updatedCompany.city.id
                const socialMediaPaths = {}
                getSocialMediaURL(this.data, socialMediaPaths, SocialMediaEnums)
                const request = {
                    name: this.company.name,
                    pib: this.company.pib,
                    address: this.company.address,
                    cityId: this.company.cityId,
                    description: this.company?.descriptionTextContent?.originalText ?? null,
                    subdomain: this.updatedCompany.subdomain ?? null,
                    theme: this.updatedCompany.theme ?? null,
                    yearlySubscription: this.updatedCompany.yearlySubscription,
                    ...socialMediaPaths,
                }
                this.submitForm(CompanyAPI.updateCompany(request, this.updatedCompany.id))
                    .then(() => {
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.isCompanyUpdateModalOpen = false
                        this.isDeactivateCompanyModalOpen = false
                        this.$auth.reloadUser()
                        this.getCompany()
                        this.emitEvent({ name: 'company-settings-updated' })
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
                    .finally(() => {
                        this.$stopLoader()
                    })
            },

            getCompany() {
                const companyId = this.$route.params.id
                CompanyAPI.getById(companyId).then((company) => {
                    this.updatedCompany = company
                })
            },

            chooseTheme(type) {
                this.updatedCompany.theme = type.toUpperCase()
            },

            deactivateCompany() {
                this.updatedCompany.requestSubscriptionRemoval = true
                this.updateCompany()
            },
        },

        watch: {
            company() {
                this.updatedCompany = this.company
                this.data.subdomain = this.company.subdomain
                this.getCurrentPackage()
                this.changeCountryName()
            },
        },

        created() {
            this.updatedCompany = this.updatedCompany ? this.updatedCompany : this.company
        },

        mounted() {
            this.prefillData(this.company)
            this.getCurrentPackage()
            this.changeCountryName()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/company/_company-update-form.scss';
</style>
